import * as React from 'react';
import { RouteComponentProps } from 'react-router-dom';
import addMonths from 'date-fns/addMonths';
import isSameMonth from 'date-fns/isSameMonth';
import subMonths from 'date-fns/subMonths';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import {
    DatesSetArg,
    EventClickArg,
    EventContentArg,
    EventInput,
    ViewApi
} from '@fullcalendar/core';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import { Translate } from '../../../../shared/LanguageService';
import { useUserContext } from '../../../../shared/context/user-context';
import { useCustomerBookingsQuery } from '../../calendar/queries/useCustomerBookingsQuery';
import { usePrefetchCustomerBookings } from '../../calendar/queries/usePrefetchCustomerBookings';
import { monthGridRange } from '../../calendar/state/monthGridRange';
import BookingDialog from '../../bookingDetails/view/BookingDialog';
import CancelBookingDialog from '../../bookingDetails/view/CancelBookingDialog';
import SuccessSnackbar from '../../../../shared/feedback/SuccessSnackbar';
import FailureSnackbar from '../../../../shared/feedback/FailureSnackbar';
import BookingEventContent from '../../calendar/view/BookingEventContent';
import SimultaneousBookingsDialog from '../../calendar/view/SimultaneousBookingsDialog';
import { limitSimultaneousBookings, SimultaneousGroup } from '../../calendar/state/limitSimultaneousBookings';
import { centerOf, ScreenPoint } from '../../shared/state/expandFromPoint';
import CalendarToolbar from '../../calendar/view/CalendarToolbar';
import CalendarSurface from '../../calendar/view/CalendarSurface';
import { seesWholeFirm } from '../../brokerFilter/state/seesWholeFirm';
import { visibleHours } from '../../calendar/state/visibleHours';
import { CalendarView, FullCalendarViewNames } from '../../calendar/state/calendarView';
import {
    CalendarFilters,
    calendarLocationFromSearch,
    searchForCalendarLocation
} from '../../calendar/state/calendarLocation';
import { useCalendarBrokersQuery } from '../../brokerFilter/queries/useCalendarBrokersQuery';
import { useUnbookedOrdersQuery } from '../../unbookedOrders/queries/useUnbookedOrdersQuery';
import UnbookedOrdersPanel from '../../unbookedOrders/view/UnbookedOrdersPanel';
import UnbookedOrdersDialog from '../../unbookedOrders/view/UnbookedOrdersDialog';
import { BookingRange, CustomerBooking } from '../../types';

const TitleId = 'customer-calendar-title';
const MaximumBookingsSideBySide = 3;
const LastColumnWeekday = 0;

type OpenedFrom<Shown> = {
    shown: Shown | null;
    open: boolean;
    from: ScreenPoint | null;
};

const Closed = { shown: null, open: false, from: null };

function shownLocation(shown: ViewApi): { view: CalendarView; anchor: Date } {
    let view: CalendarView = shown.type === FullCalendarViewNames.week ? 'week' : 'month';
    return { view, anchor: view === 'week' ? shown.activeStart : shown.currentStart };
}

function closing<Shown>(dialog: OpenedFrom<Shown>): OpenedFrom<Shown> {
    return { ...dialog, open: false };
}
const TwentyFourHourTime = { hour: '2-digit', minute: '2-digit', hour12: false } as const;

function toEvent(
    booking: CustomerBooking,
    displayOrder: number,
    simultaneous?: SimultaneousGroup
): EventInput {
    return {
        id: booking.id,
        title: booking.address || `${booking.orderCollectionNumber}:${booking.orderNumber}`,
        start: booking.start,
        end: booking.end,
        extendedProps: { booking, simultaneous, displayOrder }
    };
}

function CustomerCalendarPage({ history, location }: RouteComponentProps) {
    const { user } = useUserContext();
    const [initialLocation] = React.useState(() => calendarLocationFromSearch(location.search));
    const [view, setView] = React.useState<CalendarView>(initialLocation.view);
    const [range, setRange] = React.useState<BookingRange>(() =>
        monthGridRange(initialLocation.date || new Date())
    );
    const [title, setTitle] = React.useState('');
    const calendarRef = React.useRef<FullCalendar>(null);
    const [bookingDialog, setBookingDialog] = React.useState<OpenedFrom<CustomerBooking>>(Closed);
    const [cancelDialog, setCancelDialog] = React.useState<OpenedFrom<CustomerBooking>>(Closed);
    const [feedback, setFeedback] = React.useState<{ succeeded: boolean; text: string } | null>(
        null
    );
    const [simultaneousDialog, setSimultaneousDialog] =
        React.useState<OpenedFrom<CustomerBooking[]>>(Closed);
    const bookings = useCustomerBookingsQuery(range);
    const prefetchBookings = usePrefetchCustomerBookings();

    const showBrokerFirm = seesWholeFirm(user.details && user.details.role);
    const [onlyOwnWanted, setOnlyOwnWanted] = React.useState(initialLocation.onlyOwn);
    const onlyOwn = showBrokerFirm && onlyOwnWanted;
    const [chosenBrokerId, setChosenBrokerId] = React.useState(initialLocation.brokerId);
    const brokerId = showBrokerFirm ? chosenBrokerId : null;
    const firmBrokers = useCalendarBrokersQuery(showBrokerFirm);
    const unbooked = useUnbookedOrdersQuery();
    const isWide = useMediaQuery(useTheme().breakpoints.up('md'));
    const [unbookedPanelOpen, setUnbookedPanelOpen] = React.useState<boolean | null>(null);
    const [unbookedDialogOpen, setUnbookedDialogOpen] = React.useState(false);
    const mayBook = Boolean(unbooked.data && unbooked.data.mayBook);
    const ordersToBook = React.useMemo(
        () =>
            ((unbooked.data && unbooked.data.orders) || []).filter((order) =>
                onlyOwn ? order.isOwn : brokerId === null || order.brokerAgentId === brokerId
            ),
        [unbooked.data, onlyOwn, brokerId]
    );
    const returnTo = `${location.pathname}${location.search}`;
    const events = React.useMemo(() => {
        let all = (bookings.data || []).filter((booking) =>
            onlyOwn ? booking.isOwn : brokerId === null || booking.brokerAgentId === brokerId
        );
        if (view !== 'week') {
            return all.map((booking, index) => toEvent(booking, index));
        }
        let limited = limitSimultaneousBookings(
            all,
            MaximumBookingsSideBySide,
            (start) => start.getDay() === LastColumnWeekday
        );
        return limited.visible.map((booking, index) =>
            toEvent(booking, index, limited.groups[booking.id])
        );
    }, [bookings.data, view, onlyOwn, brokerId]);
    const hours = React.useMemo(() => visibleHours(bookings.data || []), [bookings.data]);

    function keepInAddress(shownView: CalendarView, anchor: Date, filters: CalendarFilters) {
        let search = searchForCalendarLocation(shownView, anchor, filters);
        if (history.location.search !== search) {
            history.replace({ search });
        }
    }

    function handleDatesSet(shown: DatesSetArg) {
        let { view: shownView, anchor } = shownLocation(shown.view);

        setView(shownView);
        setTitle(shown.view.title);
        setRange(monthGridRange(anchor));
        prefetchBookings(monthGridRange(subMonths(anchor, 1)));
        prefetchBookings(monthGridRange(addMonths(anchor, 1)));
        keepInAddress(shownView, anchor, { onlyOwn, brokerId });
    }

    function calendar() {
        return calendarRef.current!.getApi();
    }

    function handleOnlyOwnChange(showOnlyOwn: boolean) {
        let { view: shownView, anchor } = shownLocation(calendar().view);
        setOnlyOwnWanted(showOnlyOwn);
        keepInAddress(shownView, anchor, { onlyOwn: showOnlyOwn, brokerId });
    }

    function handleBrokerChange(nextBrokerId: number | null) {
        let { view: shownView, anchor } = shownLocation(calendar().view);
        setChosenBrokerId(nextBrokerId);
        keepInAddress(shownView, anchor, { onlyOwn, brokerId: nextBrokerId });
    }

    function handleViewChange(next: CalendarView) {
        let shown = calendar().view;
        if (next === 'week') {
            let today = new Date();
            let target = isSameMonth(today, shown.currentStart) ? today : shown.currentStart;
            calendar().changeView(FullCalendarViewNames.week, target);
        } else {
            calendar().changeView(FullCalendarViewNames.month, shown.activeStart);
        }
    }

    function handleEventClick({ event, el, jsEvent }: EventClickArg) {
        let simultaneous: SimultaneousGroup | undefined = event.extendedProps.simultaneous;
        let clickedBadge = (jsEvent.target as Element).closest('[data-simultaneous-badge]');
        if (simultaneous && clickedBadge) {
            setSimultaneousDialog({
                shown: simultaneous.bookings,
                open: true,
                from: centerOf(clickedBadge)
            });
        } else {
            setBookingDialog({
                shown: event.extendedProps.booking,
                open: true,
                from: centerOf(el)
            });
        }
    }

    function askToCancel() {
        setCancelDialog({ shown: bookingDialog.shown, open: true, from: null });
    }

    function handleCancelled(booking: CustomerBooking) {
        setCancelDialog(closing);
        setBookingDialog(closing);
        setFeedback({
            succeeded: true,
            text: Translate(
                booking.kind === 'styling'
                    ? 'view-order/basic-order-info/toastr/unbook-successful-stylist'
                    : 'view-order/basic-order-info/toastr/unbook-successful'
            )
        });
    }

    function handleCancelFailed() {
        setCancelDialog(closing);
        setFeedback({
            succeeded: false,
            text: Translate('view-order/basic-order-info/toastr/unbook-error')
        });
    }

    function openFromSimultaneous(booking: CustomerBooking, from: ScreenPoint) {
        setSimultaneousDialog(closing);
        setBookingDialog({ shown: booking, open: true, from });
    }

    function renderEventContent({ timeText, event }: EventContentArg) {
        let booking: CustomerBooking = event.extendedProps.booking;
        let simultaneous: SimultaneousGroup | undefined = event.extendedProps.simultaneous;
        return (
            <BookingEventContent
                timeText={timeText}
                title={event.title}
                brokerFirmName={showBrokerFirm ? booking.brokerFirmName : null}
                addressOnly={view === 'week'}
                simultaneous={simultaneous || null}
            />
        );
    }

    return (
        <section>
            {bookings.isError && (
                <Alert severity="error" variant="filled" sx={{ mb: 2 }}>
                    {Translate('customer-calendar/load-error')}
                </Alert>
            )}
            <CalendarToolbar
                title={title}
                titleId={TitleId}
                view={view}
                onlyOwnBookings={showBrokerFirm ? onlyOwn : null}
                brokers={firmBrokers.data || []}
                chosenBrokerId={brokerId}
                onBrokerChange={handleBrokerChange}
                onOnlyOwnBookingsChange={handleOnlyOwnChange}
                onPrevious={() => calendar().prev()}
                onNext={() => calendar().next()}
                onViewChange={handleViewChange}
            />
            {mayBook && !isWide && (
                <Button
                    variant="outlined"
                    fullWidth
                    startIcon={<EventAvailableIcon />}
                    endIcon={<Chip size="small" color="secondary" label={ordersToBook.length} />}
                    onClick={() => setUnbookedDialogOpen(true)}
                    sx={{ mb: 2, justifyContent: 'flex-start' }}
                >
                    {Translate('customer-calendar/unbooked/title')}
                </Button>
            )}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                {mayBook && isWide && (
                    <UnbookedOrdersPanel
                        orders={ordersToBook}
                        open={
                            unbookedPanelOpen === null ? ordersToBook.length > 0 : unbookedPanelOpen
                        }
                        showBrokerFirm={showBrokerFirm}
                        returnTo={returnTo}
                        onOpenChange={setUnbookedPanelOpen}
                    />
                )}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <CalendarSurface
                        variant="outlined"
                        component="section"
                        aria-labelledby={TitleId}
                        aria-busy={bookings.isFetching}
                    >
                        <FullCalendar
                            ref={calendarRef}
                            plugins={[dayGridPlugin, timeGridPlugin]}
                            initialView={FullCalendarViewNames[initialLocation.view]}
                            initialDate={initialLocation.date}
                            locale={user.details && user.details.languageKey}
                            firstDay={1}
                            height="auto"
                            headerToolbar={false}
                            dayHeaderFormat={{ weekday: 'short' }}
                            eventDisplay="block"
                            eventTimeFormat={TwentyFourHourTime}
                            slotLabelFormat={TwentyFourHourTime}
                            slotMinTime={hours.slotMinTime}
                            slotMaxTime={hours.slotMaxTime}
                            allDaySlot={false}
                            slotEventOverlap={false}
                            eventOrder="displayOrder"
                            nowIndicator
                            views={{
                                timeGridWeek: {
                                    dayHeaderFormat: { weekday: 'short', day: 'numeric' },
                                    weekNumbers: true,
                                    weekNumberCalculation: 'ISO',
                                    weekNumberContent: ({ num }: { num: number }) =>
                                        `${Translate('calendars/header/button/week')} ${num}`
                                }
                            }}
                            displayEventEnd
                            events={events}
                            datesSet={handleDatesSet}
                            eventClick={handleEventClick}
                            eventContent={renderEventContent}
                        />
                    </CalendarSurface>
                </Box>
            </Box>
            <UnbookedOrdersDialog
                orders={ordersToBook}
                open={unbookedDialogOpen}
                showBrokerFirm={showBrokerFirm}
                returnTo={returnTo}
                onClose={() => setUnbookedDialogOpen(false)}
            />
            <BookingDialog
                booking={bookingDialog.shown}
                open={bookingDialog.open}
                from={bookingDialog.from}
                showBrokerFirm={showBrokerFirm}
                returnTo={returnTo}
                onCancelBooking={askToCancel}
                onClose={() => setBookingDialog(closing)}
            />
            <CancelBookingDialog
                booking={cancelDialog.shown}
                open={cancelDialog.open}
                onAbort={() => setCancelDialog(closing)}
                onCancelled={handleCancelled}
                onFailed={handleCancelFailed}
            />
            <SuccessSnackbar
                open={feedback !== null && feedback.succeeded}
                text={feedback ? feedback.text : ''}
                onClose={() => setFeedback(null)}
            />
            <FailureSnackbar
                open={feedback !== null && !feedback.succeeded}
                text={feedback ? feedback.text : ''}
                onClose={() => setFeedback(null)}
            />
            <SimultaneousBookingsDialog
                bookings={simultaneousDialog.shown}
                open={simultaneousDialog.open}
                from={simultaneousDialog.from}
                showBrokerFirm={showBrokerFirm}
                onSelect={openFromSimultaneous}
                onClose={() => setSimultaneousDialog(closing)}
            />
        </section>
    );
}

export default CustomerCalendarPage;
